"use client";

import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
const locations = [
  {
    id: "samut-prakan",
    highlightImage: "/images/hsrc/HSRC-Highlight-SP.jpg",
    phoneDisplay: "02 757 6111 ext 3005",
  },
  {
    id: "bangkok",
    highlightImage: "/images/hsrc/HSRC-Highlight-BK.jpg",
    phoneDisplay: "02 735 3999 ext 3108",
  },
  {
    id: "chiang-mai",
    highlightImage: "/images/hsrc/HSRC-Highlight-CM.jpg",
    phoneDisplay: "053 294 999",
  },
  {
    id: "phuket",
    highlightImage: "/images/hsrc/HSRC-Highlight-PK.jpg",
    phoneDisplay: "076 562 999",
  },
] as const;

const locationNameKeys: Record<string, string> = {
  "samut-prakan": "samutPrakan",
  bangkok: "bangkok",
  "chiang-mai": "chiangMai",
  phuket: "phuket",
};

const locationAddressKeys: Record<string, string> = {
  "samut-prakan": "samutPrakan",
  bangkok: "bangkok",
  "chiang-mai": "chiangMai",
  phuket: "phuket",
};

export default function CenterInfo() {
  const t = useTranslations("center");
  const tAddr = useTranslations("centerAddress");
  const tCommon = useTranslations("common");

  const [activeLocation, setActiveLocation] = useState("samut-prakan");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [mapUrls, setMapUrls] = useState<Record<string, string>>({});
  const slideContainerRef = useRef<HTMLDivElement>(null);

  const activeCenter = locations.find((l) => l.id === activeLocation) ?? locations[0];
  const locationName = t(locationNameKeys[activeCenter.id] as any);
  const locationAddress = tAddr(locationAddressKeys[activeCenter.id] as any);
  const isParkCenter = ['bangkok', 'chiang-mai', 'phuket'].includes(activeCenter.id);

  useEffect(() => {
    setActiveLocation(locations[currentSlide].id);
  }, [currentSlide]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    fetch('/api/centers/public', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.success && Array.isArray(data.data)) {
          const urlMap: Record<string, string> = {};
          for (const center of data.data) {
            const slug = (center.codeE || '').toLowerCase().replace(/\s+/g, '-');
            if (slug && center.mapUrl) {
              urlMap[slug] = center.mapUrl;
            }
          }
          setMapUrls(urlMap);
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentSlide < locations.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <section className="pt-8 md:pt-16 pb-8 bg-white overflow-hidden">
      <div className="px-6 lg:px-[clamp(2rem,5vw,250px)]">
        <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto">
          <h2 className="text-[clamp(1.25rem,7vw,3rem)] md:text-[56px] font-bold text-center text-dark mb-6 md:mb-12">
          <span className="whitespace-nowrap">{t('titlePart1')}</span><br className="sm:hidden" /><span className="whitespace-nowrap">{t('titlePart2')}</span>
        </h2>

        <div className="hidden md:flex flex-wrap gap-2.5 justify-center mb-12">
          {locations.map((location) => {
            const isActive = activeLocation === location.id;
            return (
              <button
                key={location.id}
                onClick={() => setActiveLocation(location.id)}
                className={`flex-1 min-w-0 h-[50px] rounded-lg text-sm md:text-base lg:text-xl font-normal flex items-center justify-center gap-2 transition-all ${
                  isActive
                    ? "bg-white text-dark"
                    : "bg-white text-dark hover:text-honda-red"
                }`}
              >
                <Image
                  src={isActive ? "/images/icons/icon-center-orange.svg" : "/images/icons/icon-center-gray.svg"}
                  alt="Location Icon"
                  width={20}
                  height={20}
                  className="w-5 h-5 transition-all"
                />
                <span>{t(locationNameKeys[location.id] as any)}</span>
              </button>
            );
          })}
        </div>

        <div className="md:hidden mb-4">
          <div 
            ref={slideContainerRef}
            className="relative overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <button
              onClick={() => setCurrentSlide(currentSlide > 0 ? currentSlide - 1 : locations.length - 1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center transition-opacity hover:opacity-70 text-[#222222]"
              aria-label="Previous location"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={() => setCurrentSlide(currentSlide < locations.length - 1 ? currentSlide + 1 : 0)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center transition-opacity hover:opacity-70 text-[#222222]"
              aria-label="Next location"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div 
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {locations.map((location) => {
                const isActive = activeLocation === location.id;
                return (
                  <div key={location.id} className="min-w-full flex-shrink-0 px-4">
                    <button
                      onClick={() => {
                        const index = locations.findIndex(loc => loc.id === location.id);
                        setCurrentSlide(index);
                      }}
                      className={`w-full h-[50px] rounded-lg text-xl font-normal flex items-center justify-center gap-2 transition-all ${
                        isActive
                          ? "bg-white text-dark"
                          : "bg-white text-dark hover:text-honda-red"
                      }`}
                    >
                      <Image
                        src={isActive ? "/images/icons/icon-center-orange.svg" : "/images/icons/icon-center-gray.svg"}
                        alt="Location Icon"
                        width={20}
                        height={20}
                        className="w-5 h-5 transition-all"
                      />
                      <span>{t(locationNameKeys[location.id] as any)}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          
        </div>

        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 min-w-0 items-stretch">
          <div className="w-full lg:w-[940px] lg:max-w-[60%] lg:flex-none">
            <div className="relative w-full aspect-[940/470] lg:h-full rounded-lg overflow-hidden">
              <Image
                key={activeCenter.highlightImage}
                src={activeCenter.highlightImage}
                alt={`$<span className="whitespace-nowrap">{t('titlePart1')}</span><br className="sm:hidden" /><span className="whitespace-nowrap">{t('titlePart2')}</span> ${locationName}`}
                fill
                sizes="(max-width: 1024px) 100vw, 940px"
                className="object-cover"
                quality={90}
              />
            </div>
          </div>

          <div className="flex-1 bg-white rounded-lg flex flex-col">
            <div className="bg-white pt-5 lg:pt-0 pb-0 rounded-t-lg flex items-baseline gap-2 flex-wrap min-w-0">
              <h3 className="text-[clamp(1rem,4vw,1.375rem)] font-bold text-dark leading-tight">
                {t(isParkCenter ? 'titlePart1Park' : 'titlePart1')}{t('titlePart2')}
              </h3>
              <div className="text-[32px] font-bold text-dark leading-tight shrink-0">
                {locationName}
              </div>
            </div>

            <div className="pb-5 pt-4 flex-1 flex flex-col">
              <div className="mb-4">
                <p className="text-[13px] font-bold text-dark leading-relaxed">
                  {t("objective")}
                </p>
                <p className="text-[13px] text-gray-600 leading-relaxed mt-2">
                  {t("missionBody")}
                </p>
              </div>

              <div className="bg-honda-red rounded-lg px-4 py-3 mb-6">
                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                  <div className="flex items-center gap-3 md:flex-1 pb-2 md:pb-0 border-b md:border-b-0 border-white/30">
                    <Image
                      src="/images/icons/icon-phone-white.svg"
                      alt="Phone Icon"
                      width={20}
                      height={20}
                      className="w-5 h-5 flex-shrink-0"
                    />
                    <span className="text-xl text-white flex flex-wrap">
                      <span className="whitespace-nowrap">{activeCenter.phoneDisplay.split(' ext ')[0]}</span>
                      {activeCenter.phoneDisplay.includes(' ext ') && (
                        <span className="whitespace-nowrap ml-1">{tCommon('extLabel') + ' ' + activeCenter.phoneDisplay.split(' ext ')[1]}</span>
                      )}
                    </span>
                  </div>

                  <a
                    href={mapUrls[activeCenter.id] || "#"}
                    target={mapUrls[activeCenter.id] ? "_blank" : undefined}
                    rel={mapUrls[activeCenter.id] ? "noopener noreferrer" : undefined}
                    className="flex items-start gap-3 md:flex-1 hover:opacity-90 transition-opacity pt-2 md:pt-0"
                  >
                    <Image
                      src="/images/icons/icon-pin-white.svg"
                      alt="Location Pin Icon"
                      width={20}
                      height={20}
                      className="w-5 h-5 flex-shrink-0 mt-1"
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs text-white leading-tight">
                        {locationAddress}
                      </p>
                      <span className="text-xs text-white">{tCommon("map")}</span>
                      <Image
                        src="/images/icons/icon-arrow-white.svg"
                        alt="External link"
                        width={12}
                        height={12}
                        className="w-3 h-3 flex-shrink-0 object-contain"
                      />
                    </div>
                  </a>
                </div>
              </div>

              {false && (
              <div className="hidden md:grid grid-cols-2 gap-4 mb-6">
                {[
                  { icon: "/images/icons/icon-bike-red.svg", text: "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy" },
                  { icon: "/images/icons/icon-bike-red.svg", text: "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy" },
                  { icon: "/images/icons/icon-bike-red.svg", text: "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy" },
                  { icon: "/images/icons/icon-bike-red.svg", text: "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white rounded-lg px-4 py-3">
                    <Image
                      src={item.icon}
                      alt="Bike Icon"
                      width={24}
                      height={24}
                      className="w-6 h-6 flex-shrink-0"
                    />
                    <p className="text-[11px] text-gray-600 leading-tight">{item.text}</p>
                  </div>
                ))}
              </div>
              )}

              <div className="flex justify-end mt-auto">
                <a href={`/center/hsrc?center=${activeCenter.id}`} className="text-base text-honda-red hover:underline inline-flex items-center gap-2">
                  <span className="text-[13px] font-bold">{t("more")}</span>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full h-[5px] bg-honda-red mt-8 mb-4"></div>
        </div>
      </div>
    </section>
  );
}
